/**
 * useHorizontalCarousel — lógica compartida de los carousels de la landing.
 *
 * Encapsula:
 *   - `trackRef`: ref del contenedor scrolleable.
 *   - `onScroll`: handler throttled con `requestAnimationFrame`.
 *   - `canPrev` / `canNext`: si el botón debe estar habilitado.
 *   - `offset`: scrollLeft sincronizado (para los dot indicators).
 *   - `scrollByStep(dir)`: avanza/retrocede una tarjeta.
 *   - `scrollToIndex(i)`: salta al índice exacto.
 *   - `isDotActive(i)`: ayudante para resaltar el dot activo.
 *
 * Diseño:
 *   - `updateBounds` lee `scrollLeft` directamente del DOM (no depende de
 *     `offset`), evitando que el listener de resize se re-suscriba en cada
 *     scroll.
 *   - El handler está throttled con rAF, así un swipe largo solo dispara
 *     ~60 actualizaciones de estado por segundo en lugar de cientos.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface CarouselOptions {
  /** Ancho de cada tarjeta en px. */
  cardWidth: number;
  /** Espacio entre tarjetas en px. */
  cardGap: number;
  /** Cualquier valor que invalide los bounds (ej. `items.length`). */
  resetKey?: unknown;
}

export interface CarouselApi {
  trackRef: React.RefObject<HTMLDivElement>;
  offset: number;
  canPrev: boolean;
  canNext: boolean;
  onScroll: () => void;
  scrollByStep: (dir: 1 | -1) => void;
  scrollToIndex: (i: number) => void;
  isDotActive: (i: number) => boolean;
}

export function useHorizontalCarousel({
  cardWidth,
  cardGap,
  resetKey,
}: CarouselOptions): CarouselApi {
  const step = cardWidth + cardGap;

  const trackRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef(0);
  const [offset, setOffset] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateBounds = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const sl = track.scrollLeft;
    const maxOffset = Math.max(0, track.scrollWidth - track.clientWidth);
    setOffset(sl);
    setCanPrev(sl > 0);
    setCanNext(sl < maxOffset - 1);
  }, []);

  useEffect(() => { updateBounds(); }, [updateBounds, resetKey]);

  useEffect(() => {
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, [updateBounds]);

  useEffect(() => () => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
  }, []);

  const onScroll = useCallback(() => {
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      updateBounds();
      rafIdRef.current = 0;
    });
  }, [updateBounds]);

  const scrollByStep = useCallback((dir: 1 | -1) => {
    trackRef.current?.scrollBy({ left: dir * step, behavior: 'smooth' });
  }, [step]);

  const scrollToIndex = useCallback((i: number) => {
    trackRef.current?.scrollTo({ left: i * step, behavior: 'smooth' });
  }, [step]);

  const isDotActive = useCallback((i: number) => {
    const cardLeft = i * step;
    const cardRight = cardLeft + cardWidth;
    return offset < cardRight && offset + step > cardLeft;
  }, [offset, step, cardWidth]);

  return {
    trackRef,
    offset,
    canPrev,
    canNext,
    onScroll,
    scrollByStep,
    scrollToIndex,
    isDotActive,
  };
}
